import React, {useEffect, useRef, useState} from "react";
import ZeeroCheck from "./ZeeroCheck";
import {faChevronDown} from "@awesome.me/kit-92187fb46c/icons/classic/regular";

const {FontAwesomeIcon} = await import("@fortawesome/react-fontawesome");

export type TZeeroSelectConfirmation = {
	prevOption?: TOption;
	currentOption?: TOption;
	onConfirm?: () => void;
	onCancel?: () => void;
	show: boolean;
};

type TZeeroSelect = {
	defaultOption?: string;
	multiple?: boolean;
	options: TOption[];
	onOptionClick: (_: TOnOptionClick) => void;
	confirmationComponent?: (props: TZeeroSelectConfirmation) => React.ReactElement;
	className?: string;
};

export type TOption = {
	name: string;
	value: string | number;
	checked: boolean;
};

export type TOnOptionClick = {
	lastModified: TOption;
	options: TOption[];
};

export default function ZeeroSelect({
	defaultOption = "",
	multiple = false,
	options = [],
	onOptionClick,
	confirmationComponent,
	className = "",
}: TZeeroSelect): React.ReactElement {
	if (multiple && confirmationComponent) {
		throw new Error("'multiple' and 'confirmationComponent' parameters are not supported together");
	}

	const buttonRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [initOpen, setInitOpen] = useState<boolean>(false);
	const [open, setOpen] = useState<boolean>(false);
	const [localOptions, setLocalOptions] = useState<TOption[]>(dereference(options));
	const [label, setLabel] = useState<string>(defaultOption);
	const [confirmationPopupProps, setConfirmationPopupProps] = useState<TZeeroSelectConfirmation>({ show: false });

	useEffect(() => {
		setLocalOptions(dereference(options));
	}, [JSON.stringify(options)]);

	useEffect(() => {
		renderLabel();
	}, [JSON.stringify(localOptions)]);

	useEffect(() => {
		if (initOpen && dropdownRef.current && buttonRef.current) {
			const button = buttonRef.current;
			const dropdown = dropdownRef.current;
			const rect = button.getBoundingClientRect();
			dropdown.style.top = rect.bottom + "px";
			setOpen(true);
		} else {
			setOpen(false);
		}
	}, [initOpen]);

	const closeDropdown = () => {
		setInitOpen(false);
	};

	function dereference(obj: any) {
		return JSON.parse(JSON.stringify(obj));
	}

	const onClick = (option: TOption) => {
		const handleSelection = () => {
			if (multiple) {
				const copy = dereference(localOptions) as TOption[];
				const foundOption = copy.filter((opt) => opt.value === option.value)[0];
				foundOption.checked = !foundOption.checked;
				setLocalOptions(copy);
				onOptionClick({
					lastModified: option,
					options: copy,
				});
			} else {
				let copy = dereference(localOptions) as TOption[];
				copy = copy.map((opt) => {
					opt.checked = false;
					return opt;
				});
				copy.filter((opt) => opt.value === option.value)[0].checked = true;
				setLocalOptions(copy);
				onOptionClick({
					lastModified: option,
					options: copy,
				});
				closeDropdown();
			}
		};

		if (confirmationComponent && !multiple) {
			setConfirmationPopupProps({
				show: true,
				prevOption: localOptions.filter((option) => option.checked)[0],
				currentOption: option,
				onConfirm: () => {
					handleSelection();
					setConfirmationPopupProps({ show: false });
				},
				onCancel: () => setConfirmationPopupProps({ show: false }),
			});
		} else {
			handleSelection();
		}
	};

	const renderLabel = () => {
		const checkedOptions = localOptions.filter((option) => option.checked);
		if (checkedOptions.length === 0) {
			setLabel(defaultOption);
		} else {
			setLabel(checkedOptions.map((option) => option.name).join(", "));
		}
	};

	return (
		<div className={`zeero-select${multiple ? " multiple" : ""} ${className}`}>
			<div
				ref={buttonRef}
				className="zeero-select-button"
				onClick={(evt) => {
					evt.stopPropagation();
					setInitOpen(!initOpen);
				}}
				onWheel={(event)=>{event.stopPropagation()}}
			>
				<div className={`zeero-select-button-text${label === defaultOption ? " default" : ""}`}>{label}</div>
				<FontAwesomeIcon className="dropdown-icon" icon={faChevronDown} />
			</div>

			<div 
				ref={dropdownRef} 
				className={`zeero-select-dropdown${open ? " open" : ""}`}
				onWheel={(event) => {event.stopPropagation()}}
			>
				{localOptions.map((option) => (
					<ZeeroSelectEntry key={option.name} option={option} multiple={multiple} onOptionClick={onClick} />
				))}
			</div>

			{confirmationComponent && (
				<div className="confirmation-popup">{confirmationComponent({ ...confirmationPopupProps })}</div>
			)}
		</div>
	);
}

type TZeeroSelectEntry = {
	option: TOption;
	multiple: boolean;
	onOptionClick: (option: TOption) => void;
};

function ZeeroSelectEntry({ option, multiple, onOptionClick }: TZeeroSelectEntry) {
	return (
		<React.Fragment>
			{multiple ? (
				<ZeeroCheck
					id={option.name}
					text={option.name}
					classes={["zeero-select-option", option.checked ? "selected" : ""]}
					checked={option.checked}
					onChange={(checked) => {
						onOptionClick({ name: option.name, value: option.value, checked });
					}}
				/>
			) : (
				<div
					className={`zeero-select-option${option.checked ? " selected" : ""}`}
					onClick={(evt) => {
						evt.stopPropagation();
						onOptionClick({ name: option.name, value: option.value, checked: true });
					}}
					onWheel={(event) => {event.stopPropagation()}}
				>
					{option.name}
				</div>
			)}
		</React.Fragment>
	);
}
